import { PaymentMethod } from "../../../../common/enums";
import { type GetPaginatedPositionsInAccountWithFiltersQuery } from "../../../../graphql/supergraph.index";
import { useGetPaginatedPositionsInAccountWithFiltersQuery } from "../../../../graphql/supergraph.index";
import {
  FETCH_MORE_POSITIONS_PER_PAGE,
  INITIAL_POSITIONS_PER_PAGE,
} from "../PositionList/constants";

export const usePaginatedPositionsHook = ({
  accountId,
  paymentPrefId,
  supportedPaymentMethod,
  unsupportedPaymentMethod,
  skip,
  hasPaymentPref,
}: {
  accountId: string;
  skip: boolean;
  paymentPrefId?: string;
  supportedPaymentMethod?: PaymentMethod;
  unsupportedPaymentMethod?: PaymentMethod;
  hasPaymentPref?: boolean;
}) => {
  const { data, loading, fetchMore, error } = useGetPaginatedPositionsInAccountWithFiltersQuery(
    {
      variables: {
        accountId,
        first: INITIAL_POSITIONS_PER_PAGE,
        filters: {
          paymentPrefId,
          supportedPaymentMethod,
          unsupportedPaymentMethod,
          ...(hasPaymentPref !== undefined && { hasPaymentPref }),
        },
      },
      skip,
      fetchPolicy: "network-only",
    },
  );

  const unifiedUpdateQuery = (
    previousResult: GetPaginatedPositionsInAccountWithFiltersQuery,
    {
      fetchMoreResult,
    }: {
      fetchMoreResult: GetPaginatedPositionsInAccountWithFiltersQuery;
    },
  ) => {
    if (!fetchMoreResult?.account?.positions?.edges) {
      return previousResult;
    }

    if (!previousResult.account?.positions?.edges) {
      return fetchMoreResult;
    }

    return {
      account: {
        ...previousResult.account,
        positions: {
          ...previousResult.account.positions,
          edges: [
            ...(previousResult.account.positions?.edges ?? []),
            ...(fetchMoreResult.account.positions?.edges ?? []),
          ],
          pageInfo: fetchMoreResult.account.positions.pageInfo,
        },
      },
    };
  };

  const fetchMoreHandler = async () => {
    if (!data?.account?.positions?.pageInfo.hasNextPage || !accountId) {
      return;
    }
    await fetchMore({
      variables: {
        accountId,
        filters: {
          paymentPrefId,
          supportedPaymentMethod,
          unsupportedPaymentMethod,
          ...(hasPaymentPref !== undefined && { hasPaymentPref }),
        },
        first: FETCH_MORE_POSITIONS_PER_PAGE,
        after: data?.account?.positions?.pageInfo.endCursor,
      },
      updateQuery: unifiedUpdateQuery,
    });
  };

  return { data, loading, fetchMoreHandler, error };
};
